import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Package, Clock, Truck, Home as HomeIcon, XCircle, Printer, Bookmark, QrCode, Star, CreditCard, MessageCircle, Landmark, RefreshCw } from "lucide-react";
import api, { fmtJMD, formatApiError } from "../lib/api";
import { useFmt } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import QRCode from "../components/QRCode";
import StarRating from "../components/StarRating";

const FLOW = [
  { key: "pending", label: "Pending", icon: Clock },
  { key: "processing", label: "Processing", icon: Package },
  { key: "processing_complete", label: "Ready to ship", icon: CheckCircle2 },
  { key: "in_transit", label: "In transit", icon: Truck },
  { key: "delivered", label: "Delivered", icon: HomeIcon },
];

export default function OrderSuccess() {
  const { id } = useParams();
  const initialOrder = useLocation().state?.order;
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(initialOrder || null);
  const [settings, setSettings] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const fmt = useFmt();  // currency-aware formatter for on-screen receipt

  // Single fetch that respects whichever credential we have available.
  const fetchOnce = React.useCallback(async () => {
    const email = order?.customer_email
      || initialOrder?.customer_email
      || searchParams.get("email");
    const cfg = email ? { params: { email } } : {};
    try {
      const r = await api.get(`/orders/${id}/receipt`, cfg);
      setOrder(r.data);
      return true;
    } catch {
      return false;
    }
  }, [id, initialOrder, order?.customer_email, searchParams]);

  const manualRefresh = async () => {
    setRefreshing(true);
    await fetchOnce();
    // small visual lag so users see the spinner
    setTimeout(() => setRefreshing(false), 350);
  };

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await fetchOnce();
    };
    tick();
    // Auto-poll every 20s while the tab is open so the customer sees status changes in near real-time
    const interval = setInterval(() => {
      if (!document.hidden) tick();
    }, 20000);
    // Re-fetch immediately when the customer comes back to the tab
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);

    // Load site payment settings (banking info + WhatsApp URL)
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchOnce]);

  // Track URL we share with the customer must include their email so the
  // receipt endpoint allows guest access on refresh / bookmark.
  const customerEmail = order?.customer_email || initialOrder?.customer_email || searchParams.get("email");
  const trackUrl = customerEmail
    ? `${window.location.origin}/order/${id}?email=${encodeURIComponent(customerEmail)}`
    : `${window.location.origin}/order/${id}`;
  const scanUrl = `${window.location.origin}/scan/${id}`;
  const currentIdx = order ? FLOW.findIndex((s) => s.key === order.status) : 0;
  const isCancelled = order?.status === "cancelled";
  const isStaff = user && ["admin", "super_admin", "staff"].includes(user.role);

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-6 py-12 sm:py-16" data-testid="order-success-page">
      {/* Compact 5"×4" print label — rendered into <body> via portal so print CSS can fully isolate it */}
      {order && createPortal(
        <div className="print-label" data-testid="print-label">
          <div className="label-brand">
            <img src="/brand/fruit-blossoms.png" alt="" />
            <div className="meta">
              <div className="name">Fruit Blossoms · Fruit Pops</div>
              <div className="id">#{id.slice(0, 8).toUpperCase()}</div>
            </div>
          </div>
          <div className="label-body">
            <div className="deliver-to">
              <div className="heading">Deliver to</div>
              <div className="name">{order.customer_name}</div>
              <div className="addr">{order.address_line}</div>
              <div className="addr">{order.parish}</div>
              <div className="phone">{order.customer_phone}</div>
              <div className="day">📅 {order.delivery_day}</div>
            </div>
            <div className="qr-block">
              <QRCode value={scanUrl} size={130} />
              <div className="qr-caption">Staff scan</div>
            </div>
          </div>
          <div className="label-totals">
            <span className="items">
              {order.items.reduce((s, it) => s + (it.quantity || 1), 0)} item(s)
            </span>
            <span className="total">{fmtJMD(order.total)}</span>
          </div>
          {order.notes && <div className="label-notes">Note: {order.notes}</div>}
        </div>,
        document.body
      )}

      <div className="text-center mb-8 sm:mb-10 print:hidden">
        <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mb-4 sm:mb-5" style={{ background: "#ECFDF5" }}>
          <CheckCircle2 size={32} style={{ color: "#10B981" }} />
        </div>
        <div className="overline text-zinc-500 mb-3">Order received</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Thank you!</h1>
        <p className="mt-3 sm:mt-4 text-zinc-600 text-sm sm:text-base px-2">
          Order <code className="bg-zinc-100 px-2 py-0.5 rounded text-xs break-all">{id.slice(0, 8)}</code> — we'll confirm via email shortly.
        </p>
      </div>

      <div className={`grid grid-cols-1 ${isStaff ? "md:grid-cols-3" : ""} gap-6`}>
        {/* Summary / receipt */}
        <div className={`${isStaff ? "md:col-span-2" : ""} rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6`}>
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="font-display font-bold text-lg sm:text-xl">Order receipt</div>
            {isStaff && (
              <button onClick={() => window.print()} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1 print:hidden"
                      data-testid="print-receipt">
                <Printer size={12} /> Print
              </button>
            )}
          </div>

          {order && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
                <Row k="Name" v={order.customer_name} />
                <Row k="Email" v={order.customer_email} />
                <Row k="Phone" v={order.customer_phone} />
                {(order.fulfillment_method || "delivery") === "pickup" ? (
                  <>
                    <Row k="Fulfillment" v="Pickup at our office" />
                    <Row k="Pickup location" v="Greater Portmore, St. Catherine" />
                    <Row k="Pickup day" v={order.pickup_date || "We'll confirm when processing"} />
                  </>
                ) : (
                  <>
                    <Row k="Parish" v={order.parish} />
                    <Row k="Address" v={order.address_line} />
                    <Row k="Delivery day" v={order.delivery_day} />
                  </>
                )}
              </div>

              <div className="border-t border-zinc-200 pt-4">
                <div className="overline text-zinc-500 mb-2">Items</div>
                <ul className="text-sm space-y-1">
                  {order.items.map((it, i) => (
                    <li key={i} className="flex justify-between">
                      <span>{it.quantity}× {it.name}{it.custom_flavours?.length ? ` — ${it.custom_flavours.join(", ")}` : ""}</span>
                      <span className="font-semibold">{fmt(it.price * it.quantity)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-zinc-200 mt-4 pt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>{fmt(order.subtotal)}</span></div>
                {(order.fulfillment_method || "delivery") === "pickup" ? (
                  <div className="flex justify-between"><span className="text-zinc-500">Pickup</span><span>—</span></div>
                ) : order.delivery_fee > 0 ? (
                  <div className="flex justify-between"><span className="text-zinc-500">Delivery</span><span>{fmt(order.delivery_fee)}</span></div>
                ) : (
                  <div className="flex justify-between text-xs italic"><span className="text-zinc-500">Delivery fee</span><span className="text-zinc-500">To be confirmed by our team</span></div>
                )}
                <div className="flex justify-between text-base font-bold border-t border-zinc-200 pt-2"><span>Total</span><span>{fmt(order.total)}</span></div>
              </div>
            </>
          )}

          {/* Payment instructions — shown to customers on active orders */}
          {order && !isCancelled && order.status !== "delivered" && !isStaff && settings && (
            <div className="mt-6 pt-6 border-t border-zinc-200 print:hidden" data-testid="payment-info-panel">
              <div className="overline text-zinc-500 mb-3 inline-flex items-center gap-1.5">
                <CreditCard size={11} /> How to pay
              </div>
              {settings.payment_instructions && (
                <p className="text-sm text-zinc-700 mb-4 leading-relaxed">{settings.payment_instructions}</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {settings.banking_description && (
                  <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Landmark size={16} style={{ color: "#F74B64" }} />
                      <div className="font-semibold text-sm text-zinc-900">Bank transfer</div>
                    </div>
                    <pre className="text-xs text-zinc-700 whitespace-pre-wrap font-sans leading-relaxed break-words" data-testid="banking-description">
{settings.banking_description}
                    </pre>
                  </div>
                )}
                {settings.whatsapp_payment_url && (
                  <a
                    href={settings.whatsapp_payment_url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-xl border-2 border-[#25D366] bg-[#25D366]/5 hover:bg-[#25D366]/10 p-4 flex flex-col justify-between transition-colors"
                    data-testid="whatsapp-pay-link"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <MessageCircle size={16} style={{ color: "#25D366" }} />
                        <div className="font-semibold text-sm text-zinc-900">Pay by credit or debit card via WhatsApp</div>
                      </div>
                      <p className="text-xs text-zinc-600 leading-relaxed">
                        Message us your order number <code className="bg-white border border-zinc-200 px-1.5 py-0.5 rounded">{id.slice(0, 8).toUpperCase()}</code> and we'll send a secure card link.
                      </p>
                    </div>
                    <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#128C7E]">
                      Open WhatsApp →
                    </div>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Status tracker */}
          {order && !isCancelled && (
            <div className="border-t border-zinc-200 mt-6 pt-6">
              <div className="flex items-center justify-between mb-4 gap-2">
                <div className="overline text-zinc-500">Delivery status</div>
                <button
                  onClick={manualRefresh}
                  disabled={refreshing}
                  className="text-[11px] font-semibold text-zinc-500 hover:text-[#F74B64] inline-flex items-center gap-1 disabled:opacity-50 print:hidden"
                  data-testid="refresh-status-btn"
                  title="Check for the latest status update"
                >
                  <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
                  {refreshing ? "Refreshing…" : "Refresh"}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {FLOW.map((step, i) => {
                  const StepIcon = step.icon;
                  const done = i <= currentIdx;
                  const current = i === currentIdx;
                  return (
                    <div key={step.key}
                         className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold ${
                           current ? "bg-[#F74B64] text-white" :
                           done ? "bg-[#FFE4E8] text-[#F74B64]" : "bg-zinc-100 text-zinc-400"
                         }`}>
                      <StepIcon size={12} /> {step.label}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {isCancelled && (
            <div className="mt-6 rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-center gap-2">
              <XCircle size={16} /> This order was cancelled.
            </div>
          )}

          {/* Customer-friendly tracking link (no QR for customers) */}
          {!isStaff && order && !isCancelled && (
            <div className="mt-6 pt-6 border-t border-zinc-200 print:hidden">
              <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-4 flex items-start gap-3">
                <Bookmark size={18} className="mt-0.5 flex-shrink-0" style={{ color: "#F74B64" }} />
                <div className="text-sm">
                  <div className="font-semibold text-zinc-900">Track your order anytime</div>
                  <div className="text-zinc-600 mt-0.5">Bookmark this page or save the link below — we'll keep the status updated as your pops travel to you.</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <code className="text-[11px] bg-white border border-zinc-200 px-2 py-1 rounded font-mono break-all">{trackUrl}</code>
                    <button
                      onClick={() => { navigator.clipboard?.writeText(trackUrl); }}
                      className="text-[11px] font-semibold px-2 py-1 rounded border border-zinc-200 hover:bg-white"
                      data-testid="copy-track-link">
                      Copy link
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* QR code — STAFF / ADMIN ONLY */}
        {isStaff && (
          <div className="rounded-2xl border border-zinc-200 bg-white p-6 flex flex-col items-center text-center" data-testid="staff-qr-panel">
            <div className="overline text-zinc-500 mb-3 inline-flex items-center gap-1"><QrCode size={11} /> Staff scan code</div>
            <QRCode value={scanUrl} size={160} />
            <div className="text-[10px] font-mono text-zinc-500 mt-3 break-all">{id}</div>
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
              Scan at each step to update the customer's delivery status.
            </p>
          </div>
        )}
      </div>

      <div className="mt-10 text-center print:hidden">
        {user && user.role === "customer" ? (
          <Link to="/account" className="btn-primary inline-flex">View all my orders</Link>
        ) : isStaff ? (
          <Link to="/admin/orders" className="btn-primary inline-flex">Back to orders</Link>
        ) : (
          <Link to="/" className="btn-primary inline-flex">Back to home</Link>
        )}
      </div>

      {/* Rate-the-flavours prompt — only shown to the customer once delivered */}
      {order && order.status === "delivered" && user && user.role === "customer" && (
        <RateFlavoursPanel order={order} />
      )}
    </div>
  );
}

function RateFlavoursPanel({ order }) {
  // Build a unique list of flavour product references that this order used
  const [products, setProducts] = useState([]);
  useEffect(() => {
    const names = new Set();
    for (const it of order.items || []) {
      if (it.type === "product") names.add(it.name);
      if (it.custom_flavours) it.custom_flavours.forEach((n) => names.add(n));
    }
    api.get("/products").then((r) => {
      const all = r.data.filter((p) => names.has(p.name));
      // include preset-box flavours
      const presetIds = (order.items || []).filter((it) => it.type === "preset_box").map((it) => it.ref_id);
      if (presetIds.length === 0) { setProducts(all); return; }
      // Fetch preset boxes and map flavour names
      Promise.all(presetIds.map((id) => api.get(`/preset-boxes`).then((rr) => rr.data.find((b) => b.id === id))))
        .then((boxes) => {
          const extra = new Set();
          boxes.filter(Boolean).forEach((b) => (b.flavour_names || []).forEach((n) => extra.add(n)));
          const merged = r.data.filter((p) => names.has(p.name) || extra.has(p.name));
          setProducts(merged);
        }).catch(() => setProducts(all));
    }).catch(() => {});
  }, [order]);

  if (products.length === 0) return null;
  return (
    <section className="mt-10 print:hidden" data-testid="rate-flavours-panel">
      <div className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-1">
          <Star size={18} className="text-[#FFA62B]" />
          <div className="font-display font-bold text-lg sm:text-xl">How were the flavours?</div>
        </div>
        <p className="text-sm text-zinc-500 mb-5">Tap to rate each flavour. Reviews go live after our team approves them.</p>
        <div className="space-y-4">
          {products.map((p) => (
            <RateFlavourRow key={p.id} product={p} orderId={order.id} />
          ))}
        </div>
      </div>
    </section>
  );
}

function RateFlavourRow({ product, orderId }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!rating) { toast.error("Pick a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { product_id: product.id, order_id: orderId, rating, comment });
      toast.success(`Thanks for rating ${product.name}!`);
      setSubmitted(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="border-t border-zinc-100 pt-4 first:border-0 first:pt-0" data-testid={`rate-row-${product.id}`}>
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <div className="font-semibold flex-1 min-w-0 truncate">{product.name}</div>
        <StarRating rating={rating} onChange={setRating} size={22} testid={`rate-stars-${product.id}`} />
      </div>
      {!submitted && rating > 0 && (
        <>
          <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
                    placeholder={`Quick note about ${product.name}? (optional)`}
                    className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm" />
          <button onClick={submit} disabled={submitting}
                  className="btn-primary text-sm mt-2 disabled:opacity-50"
                  data-testid={`rate-submit-${product.id}`}>
            {submitting ? "Sending…" : "Submit review"}
          </button>
        </>
      )}
      {submitted && <div className="text-xs text-green-600 font-semibold">Thanks — pending approval.</div>}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-zinc-500">{k}</div>
      <div className="font-semibold break-words">{v}</div>
    </div>
  );
}
